/*
 * -----------------------------------------------------------------------
 *  <copyright file="line-by-category-chart.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */
import React, { useState, useEffect, useRef, useContext } from 'react';
import Highcharts from 'highcharts/esm/highcharts.js';
import 'highcharts/modules/exporting';
import 'highcharts/modules/offline-exporting';
import HighchartsReact from 'highcharts-react-official';
import {
    HC_LINE_STYLES,
    HC_STACKING_OPTIONS,
    OpraColours,
    HC_COLORS_STANDARD,
    OpraVerticalAlign,
    FormatNumberForChart,
    IOpraLineByCategoryChart,
} from 'interfaces/opra-chart.interface';
import { PanelFrameContext } from 'views/components/core/panel-frame';

const LineByCategoryChart = (chartProps: IOpraLineByCategoryChart) => {
    const {
        categories,
        getRef,
        horizontalLines,
        showLegend,
        secondaryKey,
        plotTitle,
        xAxisTitle,
        yAxisTitle,
        setLoadingComplete,
        stacking,
        hidePlotTitle,
    } = chartProps;
    const [options, setOptions] = useState<HighchartsReact.Props | null>(null);
    const charts = useRef<HighchartsReact.RefObject>(null);
    const height = useContext(PanelFrameContext);
    const [style, setStyle] = useState({
        style: {
            height: '350px',
        },
    });
    const getSeries = () => {
        const array: any = [];
        const subjects: any = chartProps?.series[0].values;
        for (let i = 0; i < subjects.length; i++) {
            array[i] = {
                name: subjects[i].subjectName,
                connectNulls: true,
                type: 'line',
                data: [],
            };
            for (const visit in subjects[i].visits) {
                array[i].data.push([subjects[i].visits[visit].index, subjects[i].visits[visit].value]);
            }
        }
        return array;
    };

    useEffect(() => {
        if (height?.height && height?.height !== style.style.height)
            setStyle({
                style: {
                    height: height?.height,
                },
            });
    }, [height?.height]);

    useEffect(() => {
        if (getRef && charts) getRef(charts?.current);
    }, [charts.current, options]);

    useEffect(() => {
        setOptions({
            credits: {
                enabled: false,
            },
            chart: {
                type: 'line',
                zoomType: 'xy',
            },
            exporting: {
                enabled: !getRef,
                chartOptions: { title: { text: plotTitle, style: { fontWeight: 'semi-bold' } } },
                fallbackToExportServer: false,
                buttons: {
                    contextButton: {
                        enabled: false,
                    },
                },
            },
            title: {
                text: hidePlotTitle ? null : plotTitle,
            },
            subtitle:
                secondaryKey === undefined
                    ? undefined
                    : {
                          text: secondaryKey.text,
                          useHTML: true,
                          verticalAlign:
                              secondaryKey.position === OpraVerticalAlign.top
                                  ? 'top'
                                  : secondaryKey.position === OpraVerticalAlign.middle
                                    ? 'middle'
                                    : 'bottom',
                          style: { display: (showLegend ?? true) ? undefined : 'none' },
                      },
            legend: {
                enabled: showLegend,
                itemWidth: 100,
                width: 400,
                maxHeight: 90,
                itemStyle: {
                    color: HC_COLORS_STANDARD[OpraColours.DarkGrey],
                    cursor: 'pointer',
                    fontWeight: 'normal',
                    fontSize: '12px',
                    textOverflow: 'ellipsis',
                },
            },
            xAxis: {
                categories,
                title: {
                    text: xAxisTitle || null,
                },
            },
            tooltip: {
                formatter() {
                    const self: any = this;
                    let result = `<b>${self.series.name}:</b>`;
                    if (self.point.site !== undefined) result += `<br/>Site: ${self.point.site}`;
                    result += `<br/>X-Axis: ${self.point.category}`;
                    result += `<br/>Y-Axis: ${FormatNumberForChart(self.point.y)}`;
                    return result;
                },
            },
            plotOptions: {
                series: {
                    marker: {
                        enabled: false,
                    },
                    stacking: HC_STACKING_OPTIONS[stacking || 0],
                },
                column: {
                    zIndex: 3,
                },
            },
            yAxis: {
                title: {
                    text: yAxisTitle,
                },
                crosshair: true,
                plotLines:
                    horizontalLines === undefined
                        ? undefined
                        : horizontalLines.map((pl) => ({
                              color: pl.color,
                              dashStyle: HC_LINE_STYLES[pl.style],
                              label: { text: pl.name },
                              value: pl.y,
                              zIndex: 1,
                          })),
            },
            series: getSeries(),
        });
    }, [chartProps]);

    if (options === null) {
        if (setLoadingComplete) setLoadingComplete();

        return <div>No data available.</div>;
    }

    return (
        <HighchartsReact
            ref={charts}
            highcharts={Highcharts}
            constructorType="chart"
            options={options}
            containerProps={style}
        />
    );
};

export default LineByCategoryChart;
